import React, { useMemo } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import "./Agent.css";
import BackArrow from "../../../components/backArrow";
import { useBecomeAgent, marketingTypeMap } from "../../../hooks/useBecomeAgent";
import { useLocationData } from "../../../data/use-location-data";
import { useGetApprovedMarketersQuery } from "../../../features/marketing/marketingApiSlice";
import { getAsset } from "../../../helpers/helper";
import { encodeId } from "../../../utils/idObfuscator";
import dummyUser from "../../../assets/images/dummy/user-avatar-img.jpg";

// Agent Card
const AgentCard = ({ marketer }) => {
  const navigate = useNavigate();
  const user = marketer?.userId || {};
  const imageSrc = user.imageUrl?.[0] ? getAsset(user.imageUrl[0]) : dummyUser;
  const displayName = user.fullname || "Anonymous";
  const industryName = marketer?.MIndustryName?.map((i) => i.name).join(", ") || "—";
  const categoryName = marketer?.MCategoryName?.map((c) => c.name).join(", ") || "—";
  const city = marketer?.MCity || user?.MCity || user?.address?.city || "—";

  const handleView = () => {
    if (marketer?.marketerId) {
      navigate(`/view-marketer-profile/${encodeId(marketer.marketerId)}`);
    }
  };

  return (
    <Col lg={4} md={6} sm={12} className="mb-4">
      <Card
        className="agent-profile-card h-100 shadow-sm"
        style={{
          borderRadius: "12px",
          border: "1px solid #e8ecf0",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          cursor: "pointer",
        }}
        onClick={handleView}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 12px 30px rgba(11,75,249,0.13)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)";
        }}
      >
        <Card.Body className="d-flex flex-column align-items-center text-center p-4">
          {/* Avatar */}
          <div
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              overflow: "hidden",
              border: "3px solid #0b4bf9",
              marginBottom: "12px",
            }}
          >
            <img
              src={imageSrc}
              alt={displayName}
              crossOrigin="anonymous"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = dummyUser;
              }}
            />
          </div>

          {/* Name */}
          <h6 className="fw-bold mb-1" style={{ color: "#111" }}>
            {displayName}
          </h6>

          {/* City */}
          <p className="text-muted mb-2" style={{ fontSize: "12px" }}>
            <i className="bi bi-geo-alt-fill me-1" style={{ color: "#0b4bf9" }}></i>
            {city}
          </p>

          {/* Industry Badge */}
          <Badge
            bg="primary"
            className="mb-1 px-3 py-1"
            style={{ fontSize: "11px", borderRadius: "20px", backgroundColor: "#0b4bf9" }}
          >
            {industryName}
          </Badge>

          {/* Category */}
          {categoryName !== "—" && (
            <p className="text-muted mt-2 mb-0" style={{ fontSize: "11px" }}>
              {categoryName}
            </p>
          )}

          {/* View Profile Button */}
          <Button
            size="sm"
            className="mt-3 w-100"
            style={{
              backgroundColor: "#0b4bf9",
              border: "none",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: 600,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleView();
            }}
          >
            View Profile
          </Button>
        </Card.Body>
      </Card>
    </Col>
  );
};

// Zone Agents Section
const ZoneAgentsSection = ({ selectedCity, selectedIndustry }) => {
  const { data: allMarketersData, isLoading } = useGetApprovedMarketersQuery(
    undefined,
    { skip: !selectedCity || !selectedIndustry }
  );

  const mappedIndustryValue = marketingTypeMap[selectedIndustry] || "";

  // transportShop / transportShopItem (and similar) are treated as the same industry
  const industryAliases = useMemo(() => {
    const aliases = new Set();
    if (mappedIndustryValue) aliases.add(mappedIndustryValue.toLowerCase());
    if (selectedIndustry) aliases.add(selectedIndustry.toLowerCase());
    if (mappedIndustryValue === "transportShop") {
      aliases.add("transportshopitem");
      aliases.add("transport");
    }
    return aliases;
  }, [mappedIndustryValue, selectedIndustry]);

  const filteredAgents = useMemo(() => {
    if (!allMarketersData?.data || !selectedCity || !selectedIndustry) return [];

    return allMarketersData.data.filter((marketer) => {
      // City match
      const agentCity =
        marketer?.MCity ||
        marketer?.selectedCity ||
        marketer?.userId?.MCity ||
        marketer?.userId?.address?.city ||
        "";

      const cityMatch =
        agentCity.trim().toLowerCase() === selectedCity.trim().toLowerCase();

      // Industry match
      const industryMatch = marketer?.MIndustryName?.some((ind) => {
        const name = ind?.name?.toLowerCase?.() || "";
        return industryAliases.has(name);
      });

      return cityMatch && industryMatch;
    });
  }, [allMarketersData, selectedCity, selectedIndustry, industryAliases]);

  if (!selectedCity || !selectedIndustry) return null;

  return (
    <div className="mt-4">
      <div
        className="p-3 mb-3 rounded"
        style={{ backgroundColor: "#eef3ff", border: "1px solid #c8d8ff" }}
      >
        <h6 className="mb-0 fw-semibold" style={{ color: "#0b4bf9" }}>
          <i className="bi bi-people-fill me-2"></i>
          Marketing Agents in{" "}
          <span className="fw-bold">{selectedCity}</span> — Industry:{" "}
          <span className="fw-bold">{selectedIndustry}</span>
        </h6>
      </div>

      {isLoading ? (
        <Row>
          {[1, 2, 3].map((i) => (
            <Col lg={4} md={6} sm={12} className="mb-4" key={i}>
              <Card
                className="h-100 shadow-sm"
                style={{ borderRadius: "12px", border: "1px solid #e8ecf0" }}
              >
                <Card.Body className="d-flex flex-column align-items-center text-center p-4">
                  <div
                    className="rounded-circle mb-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      backgroundColor: "#e9ecef",
                      animation: "pulse 1.5s infinite ease-in-out",
                    }}
                  />
                  <div
                    style={{
                      width: "120px",
                      height: "16px",
                      backgroundColor: "#e9ecef",
                      borderRadius: "4px",
                      marginBottom: "8px",
                      animation: "pulse 1.5s infinite ease-in-out",
                    }}
                  />
                  <div
                    style={{
                      width: "80px",
                      height: "12px",
                      backgroundColor: "#e9ecef",
                      borderRadius: "4px",
                      animation: "pulse 1.5s infinite ease-in-out",
                    }}
                  />
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : filteredAgents.length === 0 ? (
        <Alert variant="info" className="text-center">
          <i className="bi bi-info-circle me-2"></i>
          No marketing agents found in <strong>{selectedCity}</strong> for{" "}
          <strong>{selectedIndustry}</strong> industry yet.
        </Alert>
      ) : (
        <>
          <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
            <strong>{filteredAgents.length}</strong> agent
            {filteredAgents.length !== 1 ? "s" : ""} found in your zone
          </p>
          <Row>
            {filteredAgents.map((marketer) => (
              <AgentCard key={marketer._id} marketer={marketer} />
            ))}
          </Row>
        </>
      )}
    </div>
  );
};

// Main Component
export default function BecomeAgent() {
  const {
    selectedIndustry,
    shopCategoryone,
    Shopdatas,
    handleIndustryChange,
    handleCityChange,
    handleCountryChange,
    handleStateChange,
    postMarketingData,
    marketingdata,
    error,
    setError,
    isSubmitting,
    selectshop,
    isCheckingAgent,
    isAlreadyAgent,
  } = useBecomeAgent();

  const {
    countryList,
    stateList,
    cityList,
    handleCountryChange: locHandleCountryChange,
    handleStateSelect: locHandleStateSelect,
  } = useLocationData();

  // While we confirm whether the logged-in user is already an agent,
  // show a loader — never flash the Become Agent form on refresh.
  if (isCheckingAgent || isAlreadyAgent) {
    return (
      <div className="become-agent-page">
        <h2 className="page-title mt-5">
          {isAlreadyAgent ? "Redirecting to dashboard…" : "Become a Agent"}
        </h2>
        <Container className="form-wrap">
          <Card className="form-card">
            <Card.Body className="text-center py-5">
              <BackArrow />
              <div className="d-flex flex-column align-items-center justify-content-center mt-4">
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                  style={{ width: "2.5rem", height: "2.5rem" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  {isAlreadyAgent
                    ? "You are already a marketing agent. Opening your dashboard…"
                    : "Checking your marketing agent status…"}
                </p>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="become-agent-page">
      <h2 className="page-title mt-5">Become a Agent</h2>

      <Container className="form-wrap">
        <Card className="form-card">
          <Card.Body>
            <BackArrow />

            {error && (
              <Alert variant="danger" onClose={() => setError("")} dismissible className="mt-3">
                {error}
              </Alert>
            )}

            <Alert variant="info" className="mt-3 shadow-none border-0">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Zone Restriction Policy:</strong> You are only authorized to create agents within your own designated zone. Registrations for locations outside your assigned zone will not be allowed.
            </Alert>

            <h5 className="section-title mt-2">Marketing Details</h5>

            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Select Your Marketing Industry*</Form.Label>
                  <Form.Select
                    className="styled-select"
                    value={selectedIndustry}
                    onChange={(e) => handleIndustryChange(e.target.value)}
                  >
                    <option value="" disabled>
                      Select Marketing Industry
                    </option>
                    {Object.keys(marketingTypeMap).map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={6}>
                  <Form.Label>Select Your Marketing Category*</Form.Label>
                  <Form.Select
                    className="styled-select"
                    name="shopCategory"
                    onChange={(e) => Shopdatas(e)}
                    value={selectshop.shopCategory ? `${selectshop.shopCategory[0]}.${selectshop.shopCategory[1]}` : ""}
                  >
                    <option value="" disabled>
                      Select from given dropdown menu
                    </option>
                    {shopCategoryone.map((category) => (
                      <option key={category._id} value={`${category._id}.${category.name}`}>
                        {category.parentCategory
                          ? `${category.parentCategory} — ${category.name}`
                          : category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Country*</Form.Label>
                    <Form.Select
                      className="styled-select"
                      value={marketingdata?.MCountry || ""}
                      onChange={(e) => {
                        locHandleCountryChange(e);
                        handleCountryChange(e.target.value);
                      }}
                    >
                      <option value="" disabled>
                        Select your country
                      </option>
                      {countryList.map((c, index) => (
                        <option key={`${c}-${index}`} value={c}>
                          {c}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>State / Province*</Form.Label>
                    <Form.Select
                      className="styled-select"
                      value={marketingdata?.MProvince || ""}
                      onChange={(e) => {
                        handleStateChange(e.target.value);
                        locHandleStateSelect(e.target.value);
                      }}
                    >
                      <option value="" disabled>
                        Select your state
                      </option>
                      {stateList.map((p, index) => (
                        <option key={`${p}-${index}`} value={p}>
                          {p}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>City*</Form.Label>
                    <Form.Select className="styled-select" value={marketingdata?.MCity || ""} onChange={(e) => handleCityChange(e.target.value)}>
                      <option value="" disabled>
                        Select your city
                      </option>
                      {cityList.map((c, index) => (
                        <option key={`${c.name}-${index}`} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Check className="mb-3" label="I agree to abide by the platform rules" />

              <Button className="proceed-btn" onClick={postMarketingData} disabled={isSubmitting}>
                {isSubmitting ? "Proceeding..." : "Proceed"}
              </Button>
            </Form>

            {/* ── Show zone agents when city + industry are both selected ── */}
            <ZoneAgentsSection
              selectedCity={marketingdata?.MCity}
              selectedIndustry={selectedIndustry}
            />
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}
